import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Upload, AlertTriangle, CheckCircle, UserPlus, X, Plus, Info, Clock } from 'lucide-react';
import axios from 'axios';
import ContactModal from './ContactModal';
import Toast from './Toast';
import { withServerWakeup } from '../utils/serverWakeup';
import { useAuth } from '../App';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const FreeCheckup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [questions, setQuestions] = useState({
    business_age: '',
    bookkeeping_frequency: '',
    reconciliation_frequency: '',
    bookkeeping_cadence: '',
    months_behind: 0,
    operating_margin: '',
    owner_pay: '',
    vendor_payments: '',
    separate_business_account: null,
    sales_tax_tracked: '',
    payroll_filings_current: '',
    has_no_financial_statements: false,
    has_professional_bookkeeper: false
  });
  const [consentChecked, setConsentChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Running checkup...');
  const [results, setResults] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [checkupId, setCheckupId] = useState(null); // Store checkup ID for payment
  const fileInputRef = useRef(null);

  // Demo banner logic
  const [bannerVariant, setBannerVariant] = useState('positive');
  
  // Match modal state
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Toast state for server wake-up
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  
  // Pricing state
  const [deepAnalysisPrice, setDeepAnalysisPrice] = useState('$5');
  
  useEffect(() => {
    fetch(`${API}/pricing`).then(r => r.json()).then(d => {
      if (d.deep_analysis_price_display) setDeepAnalysisPrice(d.deep_analysis_price_display);
    }).catch(() => {});
  }, []);

  // "Talk to a Pro" opens the in-app contact form (routed to support for now).
  const handleTalkToPro = () => {
    setShowContactModal(true);
  };
  
  // Progressive loading messages with emojis
  const loadingMessages = [
    '⏳ Running your financial checkup…',
    '💡 Crunching numbers and checking ratios…',
    '🚀 Almost there! Generating your personalized insights…'
  ];
  
  useEffect(() => {
    let messageInterval;
    if (loading) {
      let messageIndex = 0;
      setLoadingMessage(loadingMessages[0]); // Set initial message
      messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % loadingMessages.length;
        setLoadingMessage(loadingMessages[messageIndex]);
      }, 2500); // Change every 2.5 seconds
    }
    return () => {
      if (messageInterval) {
        clearInterval(messageInterval);
      }
    };
  }, [loading]);

  // Dedicated demo function using fetch (not axios)
  const runDemo = async () => {
    setLoading(true);
    console.log('runDemo: Starting demo request...');
    
    try {
      console.log('runDemo: Making fetch request to:', `${API}/free-checkup`);
      const res = await fetch(`${API}/free-checkup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ demo_mode: true })
      });
      
      console.log('runDemo: Response received, status:', res.status);
      if (!res.ok) throw new Error(`Demo failed: ${res.status}`);
      
      console.log('runDemo: Parsing JSON response...');
      const data = await res.json();
      console.log('runDemo: Response data:', data);
      
      // Use existing result rendering path
      setResults(data);
      
      // Use the server-provided checkup_id (falls back only if backend didn't return one)
      const newCheckupId = data.checkup_id
        || `checkup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setCheckupId(newCheckupId);
      
      setStep(4);
      console.log('runDemo: Results set, moving to step 4');
      
      // Set demo banner as seen
      if (data.demo_mode) {
        localStorage.setItem('financeVitals.demoBannerSeen', 'true');
      }
      
    } catch (err) {
      console.error('runDemo error', err);
      alert('Demo analysis failed. Please try again.');
    }
    
    setLoading(false);
    console.log('runDemo: Loading set to false');
  };
  
  useEffect(() => {
    // Check if user has seen demo banner before and hasn't uploaded real files
    const demoBannerSeen = localStorage.getItem('financeVitals.demoBannerSeen');
    const hasUploaded = localStorage.getItem('financeVitals.hasUploaded');
    
    if (demoBannerSeen && !hasUploaded) {
      setBannerVariant('urgency');
    } else {
      setBannerVariant('positive');
    }
  }, []);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    selectedFiles.forEach(file => validateAndAddFile(file));
  };

  const validateAndAddFile = (selectedFile) => {
    if (selectedFile) {
      const allowedTypes = ['.csv', '.xlsx', '.xls', '.pdf'];
      const fileExtension = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        alert('Please upload a CSV, Excel, or PDF file.');
        return;
      }
      
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB for free checkup.');
        return;
      }

      // Check if file with same name already exists
      const existingFile = files.find(f => f.name === selectedFile.name);
      if (existingFile) {
        alert('A file with this name is already uploaded.');
        return;
      }

      setFiles(prev => [...prev, selectedFile]);
      
      // Mark that user has uploaded real files
      localStorage.setItem('financeVitals.hasUploaded', 'true');
    }
  };

  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(f => f.name !== fileName));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    droppedFiles.forEach(file => validateAndAddFile(file));
  };

  // Log API base URL in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.info('API_BASE:', BACKEND_URL);
    }
  }, []);

  const handleAnalysis = async () => {
    // For "no formal statements" path with no files, send questionnaire data as JSON
    if (questions.has_no_financial_statements && files.length === 0) {
      setLoading(true);
      
      const showToastNotification = (message, type) => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
      };
      
      const questionnaireApiCall = async () => {
        const payload = {
          questionnaire_mode: true,
          checkup_id: checkupId,
          health_questions: {
            ...questions,
            consent_timestamp: new Date().toISOString(),
            consent_given: consentChecked
          }
        };
        
        return await axios.post(`${API}/free-checkup`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      };
      
      try {
        const response = await withServerWakeup(questionnaireApiCall, showToastNotification);
        setResults(response.data);
        
        // Use the server-provided checkup_id so subsequent attach / checkout
        // calls address the same row the backend just persisted.
        const newCheckupId = response.data?.checkup_id
          || `checkup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        setCheckupId(newCheckupId);
        
        setStep(4);
      } catch (error) {
        console.error('Questionnaire analysis error:', error);
        alert('Analysis failed. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }
    
    // Safety net: defer to JSON demo instead of axios/multipart when no files
    if (!files || files.length === 0) {
      return runDemo();
    }
    
    setLoading(true);
    
    // Helper to show toast
    const showToastNotification = (message, type) => {
      setToastMessage(message);
      setToastType(type);
      setShowToast(true);
    };
    
    // Determine if we should use demo mode (no files uploaded and user has statements)
    const shouldUseDemoMode = files.length === 0 && !questions.has_no_financial_statements;
    
    const analysisApiCall = async () => {
      let response;
      
      if (shouldUseDemoMode) {
        // Clean JSON request for demo mode
        const payload = {
          demo_mode: true,
          checkup_id: checkupId,
          health_questions: {
            ...questions,
            consent_timestamp: new Date().toISOString(),
            consent_given: consentChecked
          }
        };
        
        response = await axios.post(`${API}/free-checkup`, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // Send multipart form data for file upload or no-statements mode
        const formData = new FormData();
        
        // Add all files if any exist
        if (files && files.length > 0) {
          files.forEach((file) => {
            formData.append(`files`, file);
          });
        }
        
        // Add consent timestamp and questions
        const analysisData = {
          ...questions,
          consent_timestamp: new Date().toISOString(),
          consent_given: consentChecked
        };
        formData.append('health_questions', JSON.stringify(analysisData));
        
        response = await axios.post(`${API}/free-checkup`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      return response;
    };
    
    try {
      const response = await withServerWakeup(analysisApiCall, showToastNotification);
      setResults(response.data);
      
      // Use the server-provided checkup_id (falls back only if backend didn't return one)
      const newCheckupId = response.data?.checkup_id
        || `checkup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setCheckupId(newCheckupId);
      
      // Set demo banner as seen and track analytics
      if (response.data.demo_mode) {
        localStorage.setItem('financeVitals.demoBannerSeen', 'true');
        // Analytics hook (safe no-op if not configured)
        if (typeof window !== 'undefined' && window.analytics) {
          window.analytics.track('demo_banner_viewed', { variant: bannerVariant });
        }
      }
      
      setStep(4);
    } catch (error) {
      console.error('Analysis error:', error);

      // Robustly extract a human-readable error message even when the response
      // is not JSON (e.g. Cloudflare 520 HTML, network failures, empty bodies).
      const extractMessage = (err) => {
        const resp = err && err.response;
        if (resp) {
          const data = resp.data;
          if (data && typeof data === 'object' && data.detail) return data.detail;
          if (typeof data === 'string') {
            const trimmed = data.trim();
            if (trimmed && !trimmed.toLowerCase().startsWith('<')) return trimmed.slice(0, 240);
          }
          if (resp.status === 520 || resp.status === 502 || resp.status === 503 || resp.status === 504) {
            return 'Our server had trouble processing your files. Please retry in a moment, or re-export your P&L and Balance Sheet as CSV / standard XLSX.';
          }
          if (resp.statusText) return `${resp.status} ${resp.statusText}`;
          return `Request failed with status ${resp.status}`;
        }
        if (err && err.message) return err.message;
        return 'Network error. Please check your connection and try again.';
      };

      const errorMessage = extractMessage(error);
      if (shouldUseDemoMode) {
        alert(`Demo analysis failed: ${errorMessage}. Please try again, or upload your own files. If the issue persists, contact support@finance-vitals.com.`);
      } else {
        alert(`Analysis failed: ${errorMessage} If the issue persists, contact support@finance-vitals.com.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
    setToastMessage('');
  };

  const handleUnlockDeepAnalysis = async () => {
    if (!user) {
      // Persist the checkup id so it survives the /login redirect and can be
      // attached to the newly authenticated user by Login.js.
      if (checkupId) {
        localStorage.setItem('fv_pending_checkup', checkupId);
      }
      // Redirect to login with the resume-deep-analysis path
      navigate(`/login?redirect=/resume-deep-analysis/${checkupId}`);
      return;
    }

    if (!checkupId) {
      alert('Checkup ID not found. Please try running the analysis again.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Ensure this checkup is attached to the current user before checkout so
      // the dashboard reflects it even if the user paid on a different session.
      try {
        await axios.post(
          `${API}/checkups/${checkupId}/attach`, {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (attachErr) {
        // Non-fatal (already-attached returns 200; 403 means another user owns
        // it, which shouldn't happen here). Continue to checkout regardless.
        console.warn('Pre-checkout attach warning:', attachErr?.response?.data || attachErr);
      }
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { checkup_id: checkupId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.response?.data?.detail || 'Failed to create checkout session');
    }
  };

  const handleSaveAnalysis = async () => {
    if (!user) {
      // Persist so Login.js can attach after signup+login round-trip.
      if (checkupId) {
        localStorage.setItem('fv_pending_checkup', checkupId);
      }
      // Redirect to register with the results-checkup path
      navigate(`/register?redirect=/results-checkup/${checkupId}`);
      return;
    }
    if (!checkupId) return;
    // Already signed in — attach and jump straight to the results page so the
    // dashboard shows the saved checkup.
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/checkups/${checkupId}/attach`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.warn('Attach warning:', err?.response?.data || err);
    }
    navigate(`/results-checkup/${checkupId}`);
  };

  // Resume pending action after login
  useEffect(() => {
    const resumePendingAction = async () => {
      const pendingAction = sessionStorage.getItem('pendingAction');
      const pendingCheckupId = sessionStorage.getItem('pendingCheckupId');
      
      if (user && pendingAction && pendingCheckupId) {
        // Clear pending action
        sessionStorage.removeItem('pendingAction');
        sessionStorage.removeItem('pendingCheckupId');
        
        if (pendingAction === 'deep_analysis') {
          // Resume Deep Analysis purchase
          try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
              `${API}/checkout/create-session`,
              { checkup_id: pendingCheckupId },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            window.location.href = response.data.checkout_url;
          } catch (error) {
            console.error('Resume checkout error:', error);
            alert('Failed to resume checkout. Please try again.');
          }
        } else if (pendingAction === 'save_analysis') {
          // Show success message
          setToastMessage('Your analysis has been saved to your account!');
          setToastType('success');
          setShowToast(true);
        }
      }
    };
    
    if (step === 4) {
      resumePendingAction();
    }
  }, [user, step, API, navigate]);

  const handleBannerClick = () => {
    // Analytics hook
    if (typeof window !== 'undefined' && window.analytics) {
      window.analytics.track('demo_banner_clicked', { variant: bannerVariant });
    }
    
    // Navigate back to upload step
    setStep(2);
    
    // Focus file input after a short delay
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.focus();
      }
    }, 300);
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 65) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 85) return 'bg-green-100';
    if (score >= 65) return 'bg-yellow-100';
    if (score >= 40) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getBadgeColor = (score) => {
    if (score >= 85) return 'bg-green-500';
    if (score >= 65) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getScoreBand = (score) => {
    if (score >= 85) return { band: 'Healthy', emoji: '✅' };
    if (score >= 65) return { band: 'Needs Improvement', emoji: '⚠️' };
    if (score >= 40) return { band: 'At Risk', emoji: '❗' };
    return { band: 'Critical', emoji: '🔴' };
  };

  // Step 1: Financial Statements Question
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                <TrendingUp className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
              </Link>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Free Financial Checkup</h1>
              <p className="text-gray-600">Get an instant health score without creating an account</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Financial Statements</h2>
                <div className="space-y-3">
                  {/* Radio card with improved alignment */}
                  <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="statements"
                      checked={!questions.has_no_financial_statements}
                      onChange={() => setQuestions({...questions, has_no_financial_statements: false})}
                      className="text-green-600 mt-0.5 flex-shrink-0"
                      aria-describedby="organized-desc"
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-800 leading-snug">I have organized financial statements</span>
                      <p id="organized-desc" className="text-xs text-gray-600 leading-relaxed">I can upload P&L, Balance Sheet, or other financial documents</p>
                    </div>
                  </label>
                  
                  {/* Radio card with improved alignment */}
                  <label className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="statements"
                      checked={questions.has_no_financial_statements}
                      onChange={() => setQuestions({...questions, has_no_financial_statements: true})}
                      className="text-red-600 mt-0.5 flex-shrink-0"
                      aria-describedby="no-formal-desc"
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium text-gray-800 leading-snug">I don't have formal financial statements</span>
                      <p id="no-formal-desc" className="text-xs text-gray-600 leading-relaxed">I'll skip file upload and continue with questions only</p>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="flex justify-between">
                <Link to="/" className="px-6 py-2 text-gray-600 hover:text-gray-800">← Back to Home</Link>
                <button
                  onClick={() => setStep(questions.has_no_financial_statements ? 3 : 2)}
                  className="px-8 py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {questions.has_no_financial_statements ? 'Continue to Questions' : 'Upload Files'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: File Upload (only if user has statements)
  if (step === 2) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <Link to="/" className="inline-flex items-center space-x-2 mb-6">
                <TrendingUp className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
              </Link>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Upload Financial Documents</h1>
              <p className="text-gray-600">Upload your P&L, Balance Sheet, or other financial documents</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {/* Legal Disclaimer */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-800 mb-2">⚠️ Disclaimer</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      The financial analysis provided by Finance Vitals is generated by an AI system based on the documents you upload. 
                      It is for informational purposes only and does not constitute accounting, tax, or financial advice.
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed mt-2">
                      No AI system can fully replace human professional judgment. We strongly recommend consulting a qualified 
                      accountant or bookkeeper before making any financial decisions.
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed mt-2">
                      By using this service, you acknowledge and accept these limitations.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="text-md font-semibold text-gray-800 mb-3">
                Upload Your Files <span className="text-red-600">*</span>
              </h3>
              <p className="text-sm text-gray-600 mb-4">At least one file is required to proceed</p>

              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 cursor-pointer ${
                  dragOver ? 'border-blue-400 bg-blue-50' : 
                  files.length > 0 ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".csv,.xlsx,.xls,.pdf"
                  multiple
                  className="hidden"
                />
                
                <div>
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-800 mb-2">Upload Multiple Files</p>
                  <p className="text-sm text-gray-600">CSV, Excel, or PDF files • Max 5MB each</p>
                  <p className="text-xs text-gray-500 mt-1">Drag and drop or click to select multiple files</p>
                </div>
              </div>

              {files.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Uploaded Files:</h3>
                  <div className="space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-800">{file.name}</p>
                            <p className="text-xs text-gray-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(file.name)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Consent Checkbox */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    className="mt-1 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    I understand that the results are AI-generated and for informational purposes only. 
                    They do not constitute financial advice.
                  </span>
                </label>
              </div>

              {/* "No files? Switch to questions only" link for organized path */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setQuestions({...questions, has_no_financial_statements: true});
                    setStep(1);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
                >
                  No files? Switch to questions only
                </button>
              </div>
              
              <div className="mt-6 flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800"
                >
                  ← Back
                </button>
                <button
                  id="upload-continue"
                  type="button"
                  onClick={() => { setQuestions(q => ({...q, has_no_financial_statements: false})); setStep(3); }}
                  disabled={!consentChecked || loading || files.length === 0}
                  className={`px-8 py-3 rounded-lg font-semibold ${
                    consentChecked && !loading && files.length > 0 ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{loadingMessage}</span>
                    </div>
                  ) : 'Next: Quick Questions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Enhanced Questions
  if (step === 3) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">Financial Health Questions</h1>
              <p className="text-gray-600">These questions help us provide a more accurate health score</p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {/* Disclaimer for no-file users */}
              {questions.has_no_financial_statements && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="text-sm font-semibold text-amber-800 mb-2">⚠️ Disclaimer</h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        The financial analysis provided by Finance Vitals is generated by an AI system. 
                        It is for informational purposes only and does not constitute accounting, tax, or financial advice.
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed mt-2">
                        We strongly recommend consulting a qualified accountant or bookkeeper before making any financial decisions.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Age</label>
                  <select
                    value={questions.business_age}
                    onChange={(e) => setQuestions({...questions, business_age: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    <option value="less_than_1_year">Less than 1 year</option>
                    <option value="1_to_3_years">1-3 years</option>
                    <option value="3_to_5_years">3-5 years</option>
                    <option value="more_than_5_years">More than 5 years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bookkeeping Frequency</label>
                  <select
                    value={questions.bookkeeping_cadence}
                    onChange={(e) => setQuestions({...questions, bookkeeping_cadence: e.target.value, bookkeeping_frequency: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select...</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="less_than_quarterly">Less than quarterly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Bank Reconciliation</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="reconciliation"
                        value="monthly"
                        checked={questions.reconciliation_frequency === 'monthly'}
                        onChange={() => setQuestions({...questions, reconciliation_frequency: 'monthly'})}
                        className="text-green-600 mr-2"
                      />
                      <span className="text-sm">Monthly</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="reconciliation"
                        value="quarterly"
                        checked={questions.reconciliation_frequency === 'quarterly'}
                        onChange={() => setQuestions({...questions, reconciliation_frequency: 'quarterly'})}
                        className="text-yellow-600 mr-2"
                      />
                      <span className="text-sm">Quarterly</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="reconciliation"
                        value="never"
                        checked={questions.reconciliation_frequency === 'never'}
                        onChange={() => setQuestions({...questions, reconciliation_frequency: 'never'})}
                        className="text-red-600 mr-2"
                      />
                      <span className="text-sm">Rarely or never</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bookkeeping Status</label>
                  <select
                    value={questions.months_behind}
                    onChange={(e) => setQuestions({...questions, months_behind: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="0">Current (up to date)</option>
                    <option value="1">1 month behind</option>
                    <option value="2">2 months behind</option>
                    <option value="3">3 months behind</option>
                    <option value="4">4 months behind</option>
                    <option value="5">5 months behind</option>
                    <option value="6">6 months behind</option>
                    <option value="12">12+ months behind</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Who manages your bookkeeping?</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="bookkeeper"
                        checked={questions.has_professional_bookkeeper}
                        onChange={() => setQuestions({...questions, has_professional_bookkeeper: true})}
                        className="text-green-600 mr-2"
                      />
                      <span className="text-sm">Professional bookkeeper or accountant</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="bookkeeper"
                        checked={!questions.has_professional_bookkeeper}
                        onChange={() => setQuestions({...questions, has_professional_bookkeeper: false})}
                        className="text-orange-600 mr-2"
                      />
                      <span className="text-sm">I manage it myself</span>
                    </label>
                  </div>
                </div>

                {/* Additional Business Health Questions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Operating Margin</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="margin" value="positive" checked={questions.operating_margin === 'positive'} onChange={(e) => setQuestions({...questions, operating_margin: e.target.value})} className="text-green-600 mr-2" />
                      <span className="text-sm">Positive (making profit)</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="margin" value="unknown" checked={questions.operating_margin === 'unknown'} onChange={(e) => setQuestions({...questions, operating_margin: e.target.value})} className="text-yellow-600 mr-2" />
                      <span className="text-sm">Don't know / Unsure</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="margin" value="negative" checked={questions.operating_margin === 'negative'} onChange={(e) => setQuestions({...questions, operating_margin: e.target.value})} className="text-red-600 mr-2" />
                      <span className="text-sm">Negative (losing money)</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Owner Pay Consistency</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="owner_pay" value="consistent" checked={questions.owner_pay === 'consistent'} onChange={(e) => setQuestions({...questions, owner_pay: e.target.value})} className="text-green-600 mr-2" />
                      <span className="text-sm">Consistent and regular</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="owner_pay" value="unknown" checked={questions.owner_pay === 'unknown'} onChange={(e) => setQuestions({...questions, owner_pay: e.target.value})} className="text-yellow-600 mr-2" />
                      <span className="text-sm">Varies / Unsure</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="owner_pay" value="inconsistent" checked={questions.owner_pay === 'inconsistent'} onChange={(e) => setQuestions({...questions, owner_pay: e.target.value})} className="text-red-600 mr-2" />
                      <span className="text-sm">Inconsistent or none</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Vendor Payments</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="vendor" value="on_time" checked={questions.vendor_payments === 'on_time'} onChange={(e) => setQuestions({...questions, vendor_payments: e.target.value})} className="text-green-600 mr-2" />
                      <span className="text-sm">Always on time</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="vendor" value="sometimes_late" checked={questions.vendor_payments === 'sometimes_late'} onChange={(e) => setQuestions({...questions, vendor_payments: e.target.value})} className="text-yellow-600 mr-2" />
                      <span className="text-sm">Sometimes late</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="vendor" value="often_late" checked={questions.vendor_payments === 'often_late'} onChange={(e) => setQuestions({...questions, vendor_payments: e.target.value})} className="text-red-600 mr-2" />
                      <span className="text-sm">Often late</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Separate Business Account</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="account" checked={questions.separate_business_account === true} onChange={() => setQuestions({...questions, separate_business_account: true})} className="text-green-600 mr-2" />
                      <span className="text-sm">Yes, I have a separate business bank account</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="account" checked={questions.separate_business_account === false} onChange={() => setQuestions({...questions, separate_business_account: false})} className="text-red-600 mr-2" />
                      <span className="text-sm">No, I mix personal and business</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Sales Tax Tracking</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="sales_tax" value="yes" checked={questions.sales_tax_tracked === 'yes'} onChange={(e) => setQuestions({...questions, sales_tax_tracked: e.target.value})} className="text-green-600 mr-2" />
                      <span className="text-sm">Yes, properly tracked</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="sales_tax" value="unknown" checked={questions.sales_tax_tracked === 'unknown'} onChange={(e) => setQuestions({...questions, sales_tax_tracked: e.target.value})} className="text-yellow-600 mr-2" />
                      <span className="text-sm">Not sure / Not applicable</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="sales_tax" value="no" checked={questions.sales_tax_tracked === 'no'} onChange={(e) => setQuestions({...questions, sales_tax_tracked: e.target.value})} className="text-red-600 mr-2" />
                      <span className="text-sm">No, not tracked</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Payroll Filings</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input type="radio" name="payroll" value="yes" checked={questions.payroll_filings_current === 'yes'} onChange={(e) => setQuestions({...questions, payroll_filings_current: e.target.value})} className="text-green-600 mr-2" />
                      <span className="text-sm">Yes, up to date</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="payroll" value="unknown" checked={questions.payroll_filings_current === 'unknown'} onChange={(e) => setQuestions({...questions, payroll_filings_current: e.target.value})} className="text-yellow-600 mr-2" />
                      <span className="text-sm">Not sure / No employees</span>
                    </label>
                    <label className="flex items-center">
                      <input type="radio" name="payroll" value="no" checked={questions.payroll_filings_current === 'no'} onChange={(e) => setQuestions({...questions, payroll_filings_current: e.target.value})} className="text-red-600 mr-2" />
                      <span className="text-sm">No, behind on filings</span>
                    </label>
                  </div>
                </div>

                {/* Consent Checkbox for no-file users */}
                {questions.has_no_financial_statements && (
                  <>
                    {/* Sample Analysis Info Banner */}
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-sm font-medium text-green-800">Try Our Sample Analysis</h3>
                          <p className="text-sm text-green-700 mt-1">
                            Don't have time to answer questions? Click "Try Sample Analysis" below to see how our AI analyzes financial data using example business information.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={consentChecked}
                          onChange={(e) => setConsentChecked(e.target.checked)}
                          className="mt-1 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          I understand that the results are AI-generated and for informational purposes only. 
                          They do not constitute financial advice.
                        </span>
                      </label>
                    </div>
                  </>
                )}

                {questions.has_no_financial_statements && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex items-start">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-red-800">No Financial Statements</p>
                        <p className="text-sm text-red-700">Your health score will be based on your responses to these questions only.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-8 flex justify-between">
                <button
                  onClick={() => setStep(questions.has_no_financial_statements ? 1 : 2)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800"
                >
                  ← Back
                </button>
                <div className="flex gap-3">
                  {/* Show "Try Sample Analysis" button only for no-formal-statements path */}
                  {questions.has_no_financial_statements && (
                    <button
                      id="demo-cta"
                      type="button"
                      onClick={runDemo}
                      disabled={!consentChecked || loading}
                      className={`px-6 py-3 rounded-lg font-semibold border-2 ${
                        consentChecked && !loading ? 'border-green-600 text-green-600 hover:bg-green-50' : 'border-gray-300 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Try Sample Analysis
                    </button>
                  )}
                  
                  <button
                    onClick={handleAnalysis}
                    disabled={loading || (questions.has_no_financial_statements && !consentChecked)}
                    className={`px-8 py-3 rounded-lg font-semibold ${
                      loading || (questions.has_no_financial_statements && !consentChecked) ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>{loadingMessage}</span>
                      </div>
                    ) : 'Analyze My Business'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to get progress bar color
  const getProgressBarColor = (earned, max) => {
    const percentage = (earned / max) * 100;
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Helper function to get impact badge
  const getImpactBadge = (impact) => {
    const colors = {
      'High': 'bg-red-100 text-red-800',
      'Medium': 'bg-yellow-100 text-yellow-800', 
      'Low': 'bg-green-100 text-green-800'
    };
    return colors[impact] || colors['Medium'];
  };

  // Helper function to get time badge
  const getTimeBadge = (time) => {
    const colors = {
      'Quick': 'bg-blue-100 text-blue-800',
      'Medium': 'bg-purple-100 text-purple-800',
      'Long': 'bg-gray-100 text-gray-800'
    };
    return colors[time] || colors['Medium'];
  };

  // Step 4: Redesigned Results Page with Sticky Header and Progress Bars
  if (step === 4 && results) {
    const scoreBand = getScoreBand(results.health_score);
    
    // Use backend's unified sections array (ensure consistency)
    const sections = results.sections || [];
    
    // Consistency assertion: sum of section scores should equal total
    const sectionSum = sections.reduce((sum, s) => sum + (s.score || 0), 0);
    const totalScore = results.total || results.health_score || 0;
    
    if (Math.abs(totalScore - sectionSum) > 0.5) {
      console.warn(
        `⚠️ Score mismatch detected: Total=${totalScore}, Sections sum=${sectionSum}. Difference=${Math.abs(totalScore - sectionSum).toFixed(1)}`
      );
    }
    
    // Convert sections array to progress data for rendering
    const progressData = sections.reduce((acc, section) => {
      acc[section.key] = {
        earned: Math.max(0, section.score),
        max: section.max,
        label: section.label
      };
      return acc;
    }, {});
    
    // Fallback for old format (if sections not present)
    if (sections.length === 0 && results.section_scores) {
      const sectionScores = results.section_scores;
      progressData.basics = {
        earned: Math.max(0, sectionScores.basic_bookkeeping || 0),
        max: 35,
        label: 'Basic Bookkeeping'
      };
      progressData.business = {
        earned: Math.max(0, sectionScores.business_health || 0),
        max: 35,
        label: 'Business Health'
      };
      progressData.checks = {
        earned: Math.max(0, sectionScores.accounting_quality || 0),
        max: 30,
        label: 'Quality Checks'
      };
    }

    // Enhanced red flags with impact and time
    const enhancedRedFlags = results.red_flags?.map((flag, index) => ({
      text: flag,
      impact: ['High', 'Medium', 'Low'][index % 3],
      time: ['Quick', 'Medium', 'Long'][index % 3]
    })) || [];

    // Enhanced suggestions with impact and time  
    const enhancedSuggestions = results.suggestions?.map((suggestion, index) => ({
      text: suggestion,
      impact: ['High', 'Medium', 'Low'][index % 3],
      time: ['Quick', 'Medium', 'Long'][index % 3]
    })) || [];

    // Determine the result mode: file-based if any statement was detected,
    // questionnaire-only otherwise. Used for the label + the
    // "What we found in your files" panel.
    const stmtDetected = results.statements_detected || {};
    const hasPL = !!stmtDetected.profit_loss?.found;
    const hasBS = !!stmtDetected.balance_sheet?.found;
    const isFileBased = !results.questionnaire_mode && !results.demo_mode && (hasPL || hasBS || results.has_statements);
    const modeLabel = isFileBased ? 'File-Based Financial Health' : 'Estimated Financial Health';
    const modeExplainer = isFileBased
      ? 'This score uses your answers plus the financial information detected in your uploaded file(s).'
      : 'This estimate is based only on your answers. Upload your P&L and Balance Sheet to confirm whether the score reflects your actual financials.';

    // Formatters for the "What we found" panel.
    const fmtMoney = (v) => {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return 'Could not verify';
      const n = Number(v);
      const abs = Math.abs(n);
      const sign = n < 0 ? '-' : '';
      if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
      if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
      return `${sign}$${abs.toFixed(0)}`;
    };
    const fmtRatio = (a, b) => {
      const an = Number(a), bn = Number(b);
      if (!Number.isFinite(an) || !Number.isFinite(bn) || bn === 0) return 'Could not verify';
      return (an / bn).toFixed(2);
    };
    const detectedLabel = (found) => {
      if (found === true) return 'Yes';
      if (found === false) return 'No';
      return 'Could not verify';
    };
    const balanceLabel = (bs) => {
      if (!bs) return 'Could not verify';
      if (bs.balanced === true) return 'Balanced';
      if (bs.balanced === false && bs.imbalance_amount) {
        return `Imbalanced by approx. ${fmtMoney(bs.imbalance_amount)}`;
      }
      return 'Could not fully verify';
    };
    const em = results.extracted_metrics || {};

    // Grouped red flags ("Based on your answers" vs "Based on your files").
    const rfBySource = results.red_flags_by_source || {};
    const answersRedFlags = [...(rfBySource.questionnaire || []), ...(rfBySource.mixed || [])];
    const filesRedFlags = [...(rfBySource.files || [])];


    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        {/* Sticky Score Header - Desktop */}
        <div className="hidden md:block sticky top-0 z-40 bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link 
                to="/"
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
                aria-label="Finance Vitals Home"
              >
                <TrendingUp className="w-6 h-6 text-blue-600" />
                <span className="text-lg font-bold text-gray-900">Finance Vitals</span>
              </Link>
              <div className="flex items-center space-x-4">
                <div className={`flex items-center px-4 py-2 rounded-full ${getScoreBg(results.health_score)}`}>
                  <span className={`text-2xl font-bold ${getScoreColor(results.health_score)} mr-2`}>
                    {results.health_score}/100
                  </span>
                  <span className={`text-sm font-medium ${getScoreColor(results.health_score)}`}>
                    {scoreBand.emoji} {scoreBand.band}
                  </span>
                  {results.demo_mode && (
                    <span className="ml-2 px-3 py-1 bg-yellow-200 text-yellow-900 text-xs rounded-full font-bold border-2 border-yellow-400">
                      ⚠️ SAMPLE REPORT
                    </span>
                  )}
                  {results.mode === 'questions' && (
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                      Based on questionnaire only
                    </span>
                  )}
                </div>
                <div className="hidden lg:block">
                  <p className="text-sm text-gray-600">
                    {scoreBand.band === 'Critical' && 'Your finances need immediate attention'}
                    {scoreBand.band === 'At Risk' && 'Several areas need improvement'}
                    {scoreBand.band === 'Needs Improvement' && 'You\'re on the right track with room to grow'}
                    {scoreBand.band === 'Good' && 'Solid financial foundation'}
                    {scoreBand.band === 'Excellent' && 'Outstanding financial health'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Confirm with my financial statements
                </button>
                <button
                  onClick={handleTalkToPro}
                  className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors"
                >
                  Talk to a Pro
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Link 
                to="/"
                className="absolute left-4 sm:left-8 flex items-center space-x-2 hover:opacity-80 transition-opacity"
                aria-label="Finance Vitals Home"
              >
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-bold text-gray-900">Finance Vitals</span>
              </Link>
              <h1 className="text-3xl font-bold text-gray-900">Your Financial Health Report</h1>
            </div>
            
            {/* Mobile Score Display */}
            <div className="md:hidden mb-6">
              <div className={`inline-flex items-center px-6 py-3 rounded-full ${getScoreBg(results.health_score)} mb-2`}>
                <span className={`text-3xl font-bold ${getScoreColor(results.health_score)} mr-2`}>
                  {results.health_score}/100
                </span>
                <span className={`text-sm font-medium ${getScoreColor(results.health_score)}`}>
                  {scoreBand.emoji} {scoreBand.band}
                </span>
                {results.demo_mode && (
                  <span className="ml-2 px-3 py-1 bg-yellow-200 text-yellow-900 text-xs rounded-full font-bold border-2 border-yellow-400">
                    ⚠️ SAMPLE REPORT
                  </span>
                )}
                {results.mode === 'questions' && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                    Based on questionnaire only
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {scoreBand.band === 'Critical' && 'Your finances need immediate attention'}
                {scoreBand.band === 'At Risk' && 'Several areas need improvement'}  
                {scoreBand.band === 'Needs Improvement' && 'You\'re on the right track with room to grow'}
                {scoreBand.band === 'Good' && 'Solid financial foundation'}
                {scoreBand.band === 'Excellent' && 'Outstanding financial health'}
              </p>
            </div>
          </div>

          {/* Result mode label (file-based vs questionnaire-only) */}
          <div
            className={`rounded-xl border p-4 mb-6 ${isFileBased ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
            data-testid="result-mode-banner"
          >
            <div className="flex items-start gap-3">
              <span className="text-xl" aria-hidden="true">{isFileBased ? '📂' : '📝'}</span>
              <div>
                <h3 className={`text-base font-semibold ${isFileBased ? 'text-emerald-900' : 'text-amber-900'}`}>
                  {modeLabel}
                </h3>
                <p className={`text-sm mt-1 ${isFileBased ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {modeExplainer}
                </p>
              </div>
            </div>
          </div>

          {/* "What we found in your files" — only when files were uploaded */}
          {isFileBased && (
            <div
              className="bg-white rounded-xl shadow-sm border p-6 mb-6"
              data-testid="what-we-found-panel"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-1">What we found in your files</h2>
              <p className="text-xs text-gray-500 mb-4">
                Detected directly from your uploaded statements. Empty fields mean we couldn't confidently extract that line.
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Profit &amp; Loss detected</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-pl">{detectedLabel(stmtDetected.profit_loss?.found)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Balance Sheet detected</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-bs">{detectedLabel(stmtDetected.balance_sheet?.found)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Revenue</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-revenue">{fmtMoney(em.revenue)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Net income</dt>
                  <dd className={`font-medium ${Number(em.net_income) < 0 ? 'text-red-700' : 'text-gray-900'}`} data-testid="found-netincome">{fmtMoney(em.net_income)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Current assets</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-current-assets">{fmtMoney(em.current_assets)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Current liabilities</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-current-liab">{fmtMoney(em.current_liabilities)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Current ratio</dt>
                  <dd className="font-medium text-gray-900" data-testid="found-current-ratio">{fmtRatio(em.current_assets, em.current_liabilities)}</dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-600">Balance Sheet check</dt>
                  <dd className={`font-medium ${stmtDetected.balance_sheet?.balanced === false ? 'text-red-700' : 'text-gray-900'}`} data-testid="found-bs-check">
                    {balanceLabel(stmtDetected.balance_sheet)}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {/* Progress Bars Section */}
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Score Breakdown</h2>
            <div className="space-y-6">
              {Object.entries(progressData).map(([key, data]) => (
                <div key={key}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">{data.label}</span>
                    <span className="text-sm text-gray-500">{data.earned} / {data.max}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-500 ${getProgressBarColor(data.earned, data.max)}`}
                      style={{ width: `${Math.min(100, (data.earned / data.max) * 100)}%` }}
                      role="progressbar"
                      aria-valuenow={data.earned}
                      aria-valuemin="0"
                      aria-valuemax={data.max}
                      aria-label={`${data.label}: ${data.earned} out of ${data.max}`}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary Section */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100 p-6">
            <div className="flex items-start mb-3">
              <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <h2 className="text-xl font-semibold text-gray-900">Summary</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              {results.total >= 85 
                ? "Your bookkeeping and financial health appear to be in excellent condition. Keep maintaining consistency to stay audit-ready year-round."
                : results.total >= 60
                ? "Your finances are in generally good shape, with some areas that could benefit from improvements. Review the section scores to identify the quickest wins."
                : results.total >= 30
                ? "Your financial setup shows weaknesses that may affect clarity and compliance. Addressing the lowest-scoring areas will create immediate stability."
                : "Your financial health needs urgent attention. Several foundational bookkeeping steps are missing, increasing risk of errors or compliance issues."}
            </p>
            {(results.mode === 'questions' || results.questionnaire_mode) && (
              <p className="mt-3 text-sm text-blue-700 italic">
                Note: This assessment is based on your questionnaire responses. For a more comprehensive analysis, consider uploading your financial statements.
              </p>
            )}
          </div>

          {/* Two Column Layout for Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Fix These First Panel — grouped by source when available */}
            {enhancedRedFlags.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="red-flags-panel">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Fix these first</h2>
                </div>

                {/* If we have a meaningful split (at least one file-based and one answers-based), render grouped.
                    Otherwise fall back to the flat list so questionnaire-only and demo continue to render as before. */}
                {(answersRedFlags.length > 0 && filesRedFlags.length > 0) ? (
                  <div className="space-y-5">
                    {filesRedFlags.length > 0 && (
                      <div data-testid="red-flags-files-group">
                        <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">Based on your files</h3>
                        <div className="space-y-2">
                          {filesRedFlags.map((text, i) => (
                            <div key={`f-${i}`} className="border-l-4 border-red-500 pl-4 py-2">
                              <p className="text-sm text-gray-700">{text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {answersRedFlags.length > 0 && (
                      <div data-testid="red-flags-answers-group">
                        <h3 className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">Based on your answers</h3>
                        <div className="space-y-2">
                          {answersRedFlags.map((text, i) => (
                            <div key={`a-${i}`} className="border-l-4 border-amber-500 pl-4 py-2">
                              <p className="text-sm text-gray-700">{text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {enhancedRedFlags.map((item, index) => (
                      <div key={index} className="border-l-4 border-red-500 pl-4 py-2">
                        <p className="text-sm text-gray-700 mb-2">{item.text}</p>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getImpactBadge(item.impact)}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" aria-hidden="true" />
                            {item.impact} Impact
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTimeBadge(item.time)}`}>
                            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
                            {item.time}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Next Actions Panel */}
            {enhancedSuggestions.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center mb-4">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                  <h2 className="text-xl font-semibold text-gray-900">Next actions to improve your score</h2>
                </div>
                <div className="space-y-4">
                  {enhancedSuggestions.map((item, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                      <p className="text-sm text-gray-700 mb-2">{item.text}</p>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getImpactBadge(item.impact)}`}>
                          <TrendingUp className="w-3 h-3 mr-1" aria-hidden="true" />
                          {item.impact} Impact
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTimeBadge(item.time)}`}>
                          <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
                          {item.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Professional Help CTA in Suggestions */}
                {results.demo_mode && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-green-800 font-semibold mb-2 flex items-center">
                      📞 Want help implementing these actions?
                    </p>
                    <p className="text-green-700 text-sm mb-3">
                      A certified bookkeeper can help you improve your score faster.
                    </p>
                    <button 
                      onClick={handleTalkToPro}
                      className="px-4 py-2 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-semibold rounded-lg transition-colors inline-flex items-center text-sm">
                      → Talk to a Professional Now
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deep Analysis CTA - Conditional based on gating */}
          {results.can_offer_deep_analysis ? (
            /* Show Unlock Deep Analysis for checkups with statements */
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-sm border border-purple-100 p-6">
              <div className="flex items-start mb-4">
                <Info className="w-6 h-6 text-purple-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Go deeper than the score</h2>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    This score tells you where you stand. The Deep Analysis explains why — and what to focus on first.
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Based on the financial information you shared
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Detailed risk assessment across compliance, cash flow, and operations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Prioritized 30-day, 90-day, and long-term action plans</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Opportunities for improvement with expected impact and timeline</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Service recommendations tailored to your business needs</span>
                    </li>
                  </ul>
                </div>
              </div>
              {!user && (
                <div className="mt-4 p-3 bg-purple-100 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-900 text-center">
                    💡 We'll save your analysis so you can come back to it — and build on it.
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <button
                  onClick={handleUnlockDeepAnalysis}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg"
                >
                  🔓 Unlock Deep Analysis - {deepAnalysisPrice}
                </button>
                <button 
                  onClick={handleTalkToPro}
                  className="px-6 py-3 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-semibold rounded-lg transition-colors">
                  Talk to a Professional
                </button>
              </div>
              <p className="text-xs text-gray-600 text-center mt-4">
                Refunds are available only for a verified technical issue that prevents access to your purchase.{' '}
                <a href="mailto:support@finance-vitals.com" className="text-purple-600 hover:underline" data-testid="freecheckup-support-email-link">
                  Contact support
                </a>
              </p>
            </div>
          ) : (
            /* Show Upload Statements CTA for questions-only checkups */
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-sm border border-amber-200 p-6">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">This is a good start — but it's not the full picture</h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Your answers provide direction. More detailed financial information brings precision.
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>With financial documents, you'll get:</strong>
                  </p>
                  <ul className="mt-2 space-y-1 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>More accurate scoring based on actual financial data</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Access to purchase our comprehensive Deep Analysis ({deepAnalysisPrice})</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>AI-powered insights based on your actual numbers</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <button
                  onClick={() => {
                    setStep(1);
                    setQuestions({...questions, has_no_financial_statements: false});
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg"
                >
                  📊 Add financial documents
                </button>
                <button 
                  onClick={handleTalkToPro}
                  className="px-6 py-3 border-2 border-amber-600 text-amber-700 hover:bg-amber-50 font-semibold rounded-lg transition-colors">
                  I'll do this later
                </button>
              </div>
            </div>
          )}
          {/* Save Analysis CTA - Show for non-logged-in users */}
          {!user && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl shadow-sm border border-blue-100 p-6">
              <div className="flex items-start mb-4">
                <UserPlus className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Don't lose this snapshot of your business</h2>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    You've taken the time to assess your finances. This is a great step toward healthier finances. Create a free account to save these results and come back to them when you need clarity again.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Permanent access to your analysis results</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Track improvements over time with future checkups</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Access to premium features and Deep Analysis</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <button
                  onClick={handleSaveAnalysis}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg"
                >
                  Save my results
                </button>
              </div>
            </div>
          )}

          {/* File Info */}
          {files.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-sm border p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm font-medium text-green-800">Files Analyzed</p>
                  <p className="text-sm text-green-700">Analysis based on {files.length} uploaded file{files.length > 1 ? 's' : ''}: {files.map(f => f.name).join(', ')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Demo Warning - Prominent Banner */}
          {results.demo_mode && (
            <div className="mt-6 bg-yellow-100 border-2 border-yellow-400 rounded-xl p-6 text-center">
              <div className="text-4xl mb-2">⚠️</div>
              <h3 className="text-xl font-bold text-yellow-900 mb-2">
                This is a Sample Report
              </h3>
              <p className="text-yellow-800 mb-4 text-base">
                This is an example to show you how Finance Vitals works. Upload your actual financial files to get your real score and personalized recommendations.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => setStep(2)}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors text-base"
                >
                  Confirm with my financial statements
                </button>
                <button 
                  onClick={handleTalkToPro}
                  className="px-6 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition-colors">
                  Find a Bookkeeper
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Navigation - Go Back to Profile or Home */}
        <div className="max-w-6xl mx-auto px-4 pb-8 mt-8">
          <div className="text-center">
            {user ? (
              <Link
                to={user.role === 'professional' ? '/professional' : '/individual'}
                className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                ← Go back to my profile
              </Link>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                ← Back to Home
              </Link>
            )}
          </div>
        </div>

        {/* Sticky Mobile Bottom Bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg p-4">
          <div className="flex space-x-3">
            <button
              onClick={() => setStep(2)}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors text-center"
            >
              Confirm with my financial statements
            </button>
            <button
              onClick={handleTalkToPro}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors text-center"
            >
              Talk to a Pro
            </button>
          </div>
        </div>

        {/* Contact / Talk to a Pro Modal */}
        <ContactModal
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          variant="pro_request"
          source="free_checkup_results"
        />
      </div>
    );
  }

  return (
    <>
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        variant="pro_request"
        source="free_checkup"
      />
      
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={handleCloseToast}
        duration={5000}
        position="bottom-right"
      />
    </>
  );
};

export default FreeCheckup;